// Notifications (/settings/notifications): notification preferences + the
// notification HEALTH CHECK. Opening this screen runs every ring-path check we
// can query (notifications enabled, incoming-call channel state, Do-Not-
// Disturb override, full-screen intent access, battery optimization) and, for
// each failure, explains the consequence and deep-links the exact system
// settings screen that fixes it. Checks re-run whenever the app regains focus,
// so a fix made in system Settings clears its warning on return.
//
// Native-only (all of these are Android concepts); the web build shows only
// the explanatory text. Bridged behind the shell's composition rule — probe
// isCapacitorNative() first, then dynamically import shell/capacitor/index.js
// so no @capacitor/core reaches the web bundle.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import {
  callPushBlockReason,
  requestNotificationPermission,
} from '../../push/notificationPermission.js';
import { InstallHintBody } from '../ui/InstallHint.js';
import { useScreens } from '../ScreensContext.js';
import { Section, SettingsSubScreen, buttonClass } from './chrome.js';

export type NotificationCheckId =
  | 'blocked' // notifications off entirely (POST_NOTIFICATIONS / app toggle)
  | 'channel' // incoming-call channel disabled or demoted below heads-up
  | 'dnd' // channel cannot ring through Do Not Disturb
  | 'fullScreen' // full-screen intent access off (Android 14+)
  | 'battery'; // battery optimization may kill push delivery

export interface NotificationCheckResult {
  id: NotificationCheckId;
  ok: boolean;
}

/** Test seam: the queries + the fix shortcut for every check. */
export interface NotificationHealthBridge {
  run(): Promise<NotificationCheckResult[]>;
  fix(id: NotificationCheckId): Promise<unknown>;
}

function createNotificationHealthBridge(): NotificationHealthBridge {
  // Cache the MODULE, never a plugin proxy: resolving a promise WITH a
  // Capacitor plugin proxy makes the JS thenable-check access `.then` on it,
  // which the proxy forwards as a native call that never settles (see
  // RingtoneSettingsScreen.js for the field-debugged regression).
  let mod: Promise<typeof import('../../shell/capacitor/index.js')> | undefined;
  const getModule = (): NonNullable<typeof mod> =>
    (mod ??= import('../../shell/capacitor/index.js'));

  return {
    async run() {
      const native = await getModule();
      const plugins = native.nativePlugins();
      // Each probe fails HEALTHY (ok:true): a missing plugin method on an
      // older bridge must not produce a false alarm.
      const [notif, channel, fsi, battery] = await Promise.all([
        plugins.push.getNotificationPermissionStatus().catch(() => ({ granted: true })),
        plugins.callNotification
          .getIncomingChannelStatus()
          .catch(() => ({ enabled: true, importanceOk: true, bypassDnd: true })),
        plugins.callNotification
          .getFullScreenIntentStatus()
          .catch(() => ({ needsAttention: false })),
        plugins.callService.getBatteryOptimizationStatus().catch(() => ({ exempt: true })),
      ]);
      return [
        { id: 'blocked', ok: notif.granted },
        { id: 'channel', ok: channel.enabled && channel.importanceOk },
        { id: 'dnd', ok: channel.bypassDnd },
        { id: 'fullScreen', ok: !fsi.needsAttention },
        { id: 'battery', ok: battery.exempt },
      ];
    },
    async fix(id) {
      const native = await getModule();
      const plugins = native.nativePlugins();
      switch (id) {
        case 'blocked':
          return plugins.push.openNotificationSettings();
        case 'channel':
        case 'dnd':
          return plugins.callNotification.openIncomingChannelSettings();
        case 'fullScreen':
          return plugins.callNotification.openFullScreenIntentSettings();
        case 'battery':
          return plugins.callService.requestBatteryOptimizationExemption();
      }
    },
  };
}

function NotificationHealth({ bridge }: { bridge: NotificationHealthBridge }): ReactNode {
  const t = useT();
  const [results, setResults] = useState<NotificationCheckResult[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = (): void => {
      void bridge
        .run()
        .then((r) => {
          if (!cancelled) setResults(r);
        })
        .catch(() => {
          if (!cancelled) setResults(null);
        });
    };
    check();
    // Fixes happen off-screen in system Settings; re-run on refocus so a
    // solved problem clears (and a new one appears) without reopening the tab.
    const onFocus = (): void => {
      if (document.visibilityState === 'hidden') return;
      check();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [bridge]);

  if (results === null) return null;
  const problems = results.filter((r) => !r.ok);

  if (problems.length === 0) {
    return (
      <p role="status" className="text-xs text-ok">
        {t('notifHealth.allOk')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {problems.map(({ id }) => (
        <div key={id} role="status" className="flex flex-col gap-2 text-xs text-warn">
          <p className="font-medium">{t(`notifHealth.${id}.title`)}</p>
          <p>{t(`notifHealth.${id}.body`)}</p>
          <button
            type="button"
            className={buttonClass}
            onClick={() => void bridge.fix(id).catch(() => undefined)}
          >
            {t(`notifHealth.${id}.action`)}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * The health-check section, native-only (every check is an Android concept):
 * renders nothing on the web. Guards before the hooks-bearing body so the
 * native bridge is never touched on web.
 */
export function NotificationHealthSection({
  bridge,
}: {
  /** Test seam; defaults to the real Capacitor plugins. */
  bridge?: NotificationHealthBridge;
} = {}): ReactNode {
  if (!bridge && !isCapacitorNative()) return null;
  return <NotificationHealthResolved bridge={bridge} />;
}

function NotificationHealthResolved({
  bridge,
}: {
  bridge: NotificationHealthBridge | undefined;
}): ReactNode {
  const [resolved] = useState<NotificationHealthBridge>(
    () => bridge ?? createNotificationHealthBridge(),
  );
  return <NotificationHealth bridge={resolved} />;
}

// The web permission control (the WebView has no web Notification API and uses
// the native health check instead). Reads Notification.permission and offers
// the one action the browser actually allows: request it while still
// 'default'. Once granted, it re-syncs the push subscription so a closed-tab
// ring works without a reload; once denied, the browser blocks re-prompting,
// so it explains where to re-enable. iOS Safari tabs can't be granted at all —
// they get the Home-Screen install steps instead.
type WebNotifState = 'unsupported' | 'ios-install' | 'default' | 'granted' | 'denied';

function readWebNotifState(): WebNotifState {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (callPushBlockReason() === 'ios-install') return 'ios-install';
  return Notification.permission as WebNotifState;
}

function WebNotificationControl(): ReactNode {
  const t = useT();
  const { syncPush } = useScreens();
  const [state, setState] = useState<WebNotifState>(() => readWebNotifState());
  const [busy, setBusy] = useState(false);

  // A permission changed in the browser's own UI (or another tab) reflects here
  // on refocus — same pattern as the native health check.
  useEffect(() => {
    const refresh = (): void => {
      if (document.visibilityState !== 'hidden') setState(readWebNotifState());
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const enable = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const granted = await requestNotificationPermission();
      // Granting alone doesn't create a subscription — the app only syncs on
      // launch/rotation, so kick it now (idempotent; no-op if push is off).
      if (granted) await syncPush?.();
    } finally {
      setBusy(false);
      setState(readWebNotifState());
    }
  }, [syncPush]);

  if (state === 'unsupported') {
    return <p className="text-xs text-muted">{t('notifSettings.web.unsupported')}</p>;
  }
  if (state === 'ios-install') {
    return <InstallHintBody reason="ios-install" />;
  }
  if (state === 'granted') {
    return (
      <p role="status" className="text-xs text-ok">
        {t('notifSettings.web.granted')}
      </p>
    );
  }
  if (state === 'denied') {
    return (
      <p role="status" className="text-xs text-warn">
        {t('notifSettings.web.denied')}
      </p>
    );
  }
  return (
    <button type="button" className={buttonClass} disabled={busy} onClick={() => void enable()}>
      {t('notifSettings.web.enable')}
    </button>
  );
}

/** Web-only permission control (native uses the health check above instead). */
function WebNotificationSection(): ReactNode {
  if (isCapacitorNative()) return null;
  return <WebNotificationControl />;
}

export function NotificationsSettingsScreen(): ReactNode {
  const t = useT();
  return (
    <SettingsSubScreen section="notifications" title={t('notifSettings.title')}>
      <Section title={t('notifSettings.callNotifications')}>
        <p className="text-xs text-muted">{t('notifSettings.body')}</p>
        <NotificationHealthSection />
        <WebNotificationSection />
      </Section>
    </SettingsSubScreen>
  );
}
